import moment from 'moment'

type Rule = NonNullable<App.Checkout.TravellerFormSchemaData['allTravellerRules']>[number]

interface MatchResult {
  assignment?: Record<string, Rule['flightType']>
  missingFlightTypes: Array<Rule['flightType']>
}

function getEligibleSlotIndexes(form: App.Checkout.TravellerForm | undefined, slots: Array<Rule>): Array<number> {
  const dob = form?.data.dateOfBirth
  if (!dob || !moment.utc(dob, 'YYYY-MM-DD', true).isValid()) return []

  const tourType = form.id.split('-')[0]
  const eligibleIndexes: Array<number> = []
  for (const [slotIndex, rule] of slots.entries()) {
    if (rule.tourType !== tourType) continue
    if (rule.minDob && dob < rule.minDob) continue
    if (rule.maxDob && dob > rule.maxDob) continue
    eligibleIndexes.push(slotIndex)
  }
  return eligibleIndexes
}

/** Match each tour form to one priced flight passenger slot, including overlapping DOB ranges. */
export function checkTourFlightTravellerRules(
  formsById: Record<string, App.Checkout.TravellerForm>,
  rules: Array<Rule>,
): MatchResult {
  const slots: Array<Rule> = []
  for (const rule of rules) {
    for (let slot = 0; slot < rule.count; slot++) {
      slots.push(rule)
    }
  }
  if (slots.length === 0) return { missingFlightTypes: [] }

  const tourTypes: Array<Rule['tourType']> = ['adult', 'child']
  const forms: Array<App.Checkout.TravellerForm | undefined> = []
  for (const tourType of tourTypes) {
    const count = slots.filter(rule => rule.tourType === tourType).length
    for (let number = 1; number <= count; number++) {
      forms.push(formsById[`${tourType}-${number}`])
    }
  }
  const eligibleSlots = forms.map(form => getEligibleSlotIndexes(form, slots))

  const formBySlot = Array<number | undefined>(slots.length).fill(undefined)
  const assignForm = (formIndex: number, visitedSlots: Set<number>): boolean => {
    for (const slotIndex of eligibleSlots[formIndex]) {
      if (visitedSlots.has(slotIndex)) continue
      visitedSlots.add(slotIndex)

      const currentForm = formBySlot[slotIndex]
      // Move the current form to another eligible slot before taking this one.
      if (currentForm === undefined || assignForm(currentForm, visitedSlots)) {
        formBySlot[slotIndex] = formIndex
        return true
      }
    }
    return false
  }

  for (let formIndex = 0; formIndex < forms.length; formIndex++) {
    assignForm(formIndex, new Set<number>())
  }

  const missingFlightTypes: Array<Rule['flightType']> = []
  for (const [slotIndex, formIndex] of formBySlot.entries()) {
    const flightType = slots[slotIndex].flightType
    if (formIndex === undefined && !missingFlightTypes.includes(flightType)) {
      missingFlightTypes.push(flightType)
    }
  }
  if (missingFlightTypes.length > 0) return { missingFlightTypes }

  const assignment: Record<string, Rule['flightType']> = {}
  for (const [slotIndex, formIndex] of formBySlot.entries()) {
    if (formIndex !== undefined && forms[formIndex]) {
      assignment[forms[formIndex].id] = slots[slotIndex].flightType
    }
  }
  return {
    assignment,
    missingFlightTypes: [],
  }
}

export function matchTourFlightTravellerRules(
  formsById: Record<string, App.Checkout.TravellerForm>,
  rules: Array<Rule>,
): Record<string, Rule['flightType']> | undefined {
  return checkTourFlightTravellerRules(formsById, rules).assignment
}
